import { useCallback, useRef, useState } from 'react';
import { logEvent, EVENTS } from '../services/AnalyticsService';
import AdminAuthService from '../services/AdminAuthService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import {
  announceUploadFailed,
  announceUploadPreparing,
  uploadFile,
} from '../services/UploadService';
import { canAddPageMedia, getMediaLimitMessage } from '../services/mediaLimits';

const STORAGE_FOLDER_BY_KIND = {
  image: 'images',
  photo: 'images',
  video: 'videos',
  audio: 'audio',
};

const UPLOAD_TYPE_BY_KIND = {
  image: 'image',
  photo: 'photo',
  video: 'video',
  audio: 'audio',
};

const normalizeMediaKind = (mediaKind) => {
  if (mediaKind === 'photo') return 'image';
  return mediaKind || 'image';
};

const makeSafeFileName = (fileName = 'media') => {
  return String(fileName)
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 120);
};

const useMediaUpload = ({
  pageId,
  moduleType = 'media',
  moduleId = '',
  mediaUsage,
} = {}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const lastRequestRef = useRef(null);

  const clearUploadError = useCallback(() => setUploadError(''), []);

  const uploadAdminFile = useCallback(async (file, options = {}) => {
    if (!file) return null;

    const normalizedKind = normalizeMediaKind(options.mediaKind || options.type || 'image');
    const storageFolder = options.storageFolder || STORAGE_FOLDER_BY_KIND[normalizedKind] || 'images';
    const uploadType = options.uploadType || UPLOAD_TYPE_BY_KIND[normalizedKind] || normalizedKind;
    const replacingExisting = Boolean(options.replacingExisting);
    const displayName = file.name || options.fileName || 'media file';

    lastRequestRef.current = { file, options };

    if (!replacingExisting && mediaUsage && !canAddPageMedia(mediaUsage, normalizedKind)) {
      const message = getMediaLimitMessage(mediaUsage, normalizedKind);
      setUploadError(message);
      announceUploadFailed(normalizedKind, new Error(message), displayName);
      return null;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      announceUploadPreparing(normalizedKind, displayName);
      logEvent(pageId, EVENTS.UPLOAD_START, {
        moduleType,
        moduleId,
        mediaType: normalizedKind,
        fileSize: file.size,
      });

      const ticket = await AdminAuthService.createUploadTicket(pageId, storageFolder);
      const fileName = options.fileName ||
        `${Date.now()}_${options.index != null ? `${options.index}_` : ''}${makeSafeFileName(displayName)}`;
      const uploadedUrl = await uploadFile(
        file,
        uploadType,
        `${ticket.authorizedPath}${fileName}`,
        setUploadProgress
      );

      logEvent(pageId, EVENTS.UPLOAD_SUCCESS, {
        moduleType,
        moduleId,
        mediaType: normalizedKind,
        fileSize: file.size,
      });

      options.onSuccess?.(uploadedUrl, file);
      return uploadedUrl;
    } catch (error) {
      const message = `Upload failed: ${error.message}`;
      setUploadError(message);
      announceUploadFailed(normalizedKind, error, displayName);
      logEvent(pageId, EVENTS.UPLOAD_FAILED, {
        moduleType,
        moduleId,
        mediaType: normalizedKind,
        errorMessage: error.message,
      });
      options.onError?.(error);
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [mediaUsage, moduleId, moduleType, pageId]);

  const uploadGuestContributionFile = useCallback(async (file, options = {}) => {
    if (!file) return null;

    const normalizedKind = normalizeMediaKind(options.mediaKind || options.type || 'image');
    const uploadType = UPLOAD_TYPE_BY_KIND[normalizedKind] || normalizedKind;
    const ticketMediaType = normalizedKind === 'video' ? 'videos' : 'images';
    const displayName = file.name || options.fileName || 'media file';

    lastRequestRef.current = { file, options: { ...options, guestContribution: true } };

    if (mediaUsage && !canAddPageMedia(mediaUsage, normalizedKind)) {
      const message = getMediaLimitMessage(mediaUsage, normalizedKind);
      setUploadError(message);
      announceUploadFailed(normalizedKind, new Error(message), displayName);
      return null;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      announceUploadPreparing(normalizedKind, displayName);
      logEvent(pageId, EVENTS.UPLOAD_START, {
        moduleType,
        moduleId,
        mediaType: normalizedKind,
        fileSize: file.size,
      });

      const createGuestUploadTicket = httpsCallable(functions, 'createGuestUploadTicket');
      const addGuestContribution = httpsCallable(functions, 'addGuestContribution');
      const ticketResult = await createGuestUploadTicket({
        pageId,
        moduleId,
        mediaType: ticketMediaType,
        inviteToken: options.inviteToken || '',
      });
      const { ticketId, storagePath } = ticketResult.data || {};
      const uploadedUrl = await uploadFile(file, uploadType, storagePath, setUploadProgress);

      await addGuestContribution({
        pageId,
        moduleId,
        contributorName: options.contributorName || 'Guest',
        message: options.message || `${normalizedKind} contribution`,
        imageUrl: uploadedUrl,
        ticketId,
        inviteToken: options.inviteToken || '',
      });

      logEvent(pageId, EVENTS.UPLOAD_SUCCESS, {
        moduleType,
        moduleId,
        mediaType: normalizedKind,
        fileSize: file.size,
      });

      options.onSuccess?.(uploadedUrl, file);
      return uploadedUrl;
    } catch (error) {
      const message = `Upload failed: ${error.message}`;
      setUploadError(message);
      announceUploadFailed(normalizedKind, error, displayName);
      logEvent(pageId, EVENTS.UPLOAD_FAILED, {
        moduleType,
        moduleId,
        mediaType: normalizedKind,
        errorMessage: error.message,
      });
      options.onError?.(error);
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [mediaUsage, moduleId, moduleType, pageId]);

  const retryLastUpload = useCallback(() => {
    const lastRequest = lastRequestRef.current;
    if (!lastRequest) return Promise.resolve(null);
    if (lastRequest.options?.guestContribution) {
      return uploadGuestContributionFile(lastRequest.file, lastRequest.options);
    }
    return uploadAdminFile(lastRequest.file, lastRequest.options);
  }, [uploadAdminFile, uploadGuestContributionFile]);

  return {
    isUploading,
    uploadProgress,
    uploadError,
    setUploadError,
    clearUploadError,
    retryLastUpload,
    uploadAdminFile,
    uploadGuestContributionFile,
    mediaUploadShellProps: {
      isUploading,
      progress: uploadProgress,
      uploadError,
      onDismissError: clearUploadError,
      onRetry: retryLastUpload,
    },
  };
};

export default useMediaUpload;
